import { deleteContent, getContent } from "@/sideEffects/api";
import { selectActiveFirm, selectSelectedFolderGuid } from "@/store/appSelectors";
import { useDispatch, useSelector } from "react-redux";
import { AxMediaPreviewContent } from "@/components/ax/mediaGallery/AxMediaPreviewContent";
import { AxMediaViewer } from "@/components/ax/mediaGallery/AxMediaViewer";
import { showApiError } from "@/lib/toast";
import { appActions } from "@/store/appSlice";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { DropzoneOptions } from "react-dropzone";
import { ContentFolderView } from "./components/folder/ContentFolderView";
import { ContentTypeUI, CreateContentMenu } from "./CreateContentMenu";
import { CreateFromTemplateSheet } from "./CreateFromTemplate";
import { CreateLinkContent } from "./CreateLinkContent";
import { UploadContentSheet } from "./UploadContentSheet";
import { CreateSplitContent } from "./CreateSplitContent";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FolderIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ContentCrudFilterForm, FilterFormSchemaType } from "./ContentCrudFilterForm";
import { useSearchParams } from "react-router";
import { CreateFromStockGallery } from "./CreateFromStockGallery";

export interface ContentSelectConfig {
  onSelect: (values: {
    contentGuid: string;
    contentStorageName: string;
    contentType: string;
    contentIntId: number;
    contentDuration?: number;
  }) => void;
  defaultScreenSizeGuid?: string;
  types: ContentTypeUI[];
}

interface Props {
  selectConfig?: ContentSelectConfig;
}

const QUERY_KEY = "content";

export const ContentCrud: React.FC<Props> = ({ selectConfig }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const activeFirm = useSelector(selectActiveFirm);
  const selectedFolderGuid = useSelector(selectSelectedFolderGuid);
  const [filterValues, setFilterValues] = useState<FilterFormSchemaType>({
    screenSizeId: selectConfig?.defaultScreenSizeGuid || "",
    tagId: "",
  });
  const [queryParams] = useSearchParams();
  const showContentOptionsDialog = queryParams.get("showContentOptionsDialog") == "true";

  const setSelectedFolderGuid = (folderGuid?: string) => {
    dispatch(appActions.setSelectedFolderGuid(folderGuid || ""));
  };

  const [selectedContentTypeUI, setSelectedContentTypeUI] = useState<ContentTypeUI>();

  const isCustom = false;

  const queryClient = useQueryClient();
  const deleteMutation = useMutation({
    mutationFn: (contentGuid: string) => deleteContent(String(activeFirm?.firmGuid), contentGuid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
    onError: showApiError,
  });

  const getDropzoneOptions = (): DropzoneOptions => {
    if (selectedContentTypeUI === "image") {
      return {
        accept: {
          "image/*": [],
        },
        multiple: true,
        maxFiles: 1,
      };
    }

    if (selectedContentTypeUI === "video") {
      return {
        accept: {
          "video/*": [],
        },
        multiple: true,
        maxFiles: 1,
      };
    }

    if (selectedContentTypeUI === "audio") {
      return {
        accept: {
          "audio/*": [],
        },
        multiple: true,
        maxFiles: 1,
      };
    }

    if (selectedContentTypeUI === "doc") {
      return {
        accept: {
          "application/pdf": [],
        },
        multiple: true,
        maxFiles: 1,
      };
    }

    // Fallback
    return {
      accept: {
        "image/*": [],
        "video/*": [],
        "audio/*": [],
      },
      multiple: true,
      maxFiles: 1,
    };
  };

  const folderView = (
    <ContentFolderView onSelectFolder={setSelectedFolderGuid} selectedFolderGuid={selectedFolderGuid} />
  );

  const [isContentMenuOpen, setIsContentMenuOpen] = useState(showContentOptionsDialog);

  return (
    <div className="flex flex-col lg:flex-row gap-4">
      {isContentMenuOpen && <div className="fixed inset-0 bg-black/50 z-40" />}
      <div className="lg:w-[300px] lg:flex-shrink-0">
        <div className="block lg:hidden">
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="outline" className="w-full">
                <FolderIcon className="w-4 h-4 mr-2" />
                {t("folder.selectFolder")}
              </Button>
            </DialogTrigger>
            <DialogContent className="h-[90vh]">{folderView}</DialogContent>
          </Dialog>
        </div>
        <div className="hidden lg:block lg:sticky lg:top-4">{folderView}</div>
      </div>
      <div className="w-full">
        <AxMediaViewer
          uniqQueryKey={QUERY_KEY}
          fetcher={getContent}
          dynamicRequestParams={{
            FirmGuid: activeFirm?.firmGuid,
            isCustom,
            FolderGuid: selectedFolderGuid,
            ScreenSizeGuid: filterValues.screenSizeId,
            TagGuid: filterValues.tagId,
          }}
          itemKey="contentGuid"
          renderItem={(item) => (
            <AxMediaPreviewContent
              contentGuid={item.contentGuid}
              isDeleting={deleteMutation.isPending && deleteMutation.variables === item.contentGuid}
              onClickDelete={() => {
                deleteMutation.mutate(item.contentGuid);
              }}
              showActions
              selectConfig={selectConfig}
            />
          )}
          onDeleteItems={async (ids) => {
            await Promise.all(ids.map((id) => deleteMutation.mutate(id)));
            queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
          }}
          extraHeader={
            <div className="flex flex-row gap-2">
              <ContentCrudFilterForm onChange={setFilterValues} defaultValues={filterValues} />
              {!selectConfig && (
                <CreateContentMenu
                  defaultOpen={isContentMenuOpen}
                  onSelect={setSelectedContentTypeUI}
                  onOpenChange={setIsContentMenuOpen}
                />
              )}
              {selectedContentTypeUI === "fromTemplate" && (
                <CreateFromTemplateSheet
                  key={selectedContentTypeUI}
                  onUploadSuccess={() => {
                    queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
                    setSelectedContentTypeUI(undefined);
                  }}
                  onOpenChange={() => setSelectedContentTypeUI(undefined)}
                />
              )}
              {(selectedContentTypeUI === "image" ||
                selectedContentTypeUI === "video" ||
                selectedContentTypeUI === "audio" ||
                selectedContentTypeUI === "doc") && (
                <UploadContentSheet
                  key={selectedContentTypeUI}
                  typeUI={selectedContentTypeUI}
                  dropzoneOptions={getDropzoneOptions()}
                  onUploadSuccess={() => {
                    queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
                    setSelectedContentTypeUI(undefined);
                  }}
                  onOpenChange={() => setSelectedContentTypeUI(undefined)}
                />
              )}
              {selectedContentTypeUI === "link" && (
                <CreateLinkContent
                  onSuccess={() => {
                    queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
                    setSelectedContentTypeUI(undefined);
                  }}
                  onOpenChange={() => setSelectedContentTypeUI(undefined)}
                />
              )}
              {selectedContentTypeUI === "split" && (
                <CreateSplitContent
                  onSuccess={() => {
                    queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
                    setSelectedContentTypeUI(undefined);
                  }}
                  onOpenChange={() => setSelectedContentTypeUI(undefined)}
                />
              )}
              {selectedContentTypeUI === "fromStockGallery" && (
                <CreateFromStockGallery
                  onSuccess={() => {
                    queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
                    setSelectedContentTypeUI(undefined);
                  }}
                  onOpenChange={() => setSelectedContentTypeUI(undefined)}
                />
              )}
            </div>
          }
        />
      </div>
    </div>
  );
};
